import React, { useState, useEffect, useMemo } from 'react'
import { translate } from 'react-polyglot'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Field, reduxForm, change as changeFieldValue } from 'redux-form'
import useAxios from 'axios-hooks'

import history from 'configureHistory'
import { required, minLength6, email } from 'utils/validation'
import { createSlug } from 'utils/common'

import { Form, FormFieldset, Button, Text, Notice } from 'components'
import { Text as TextInput, Email, Password, Url } from 'components/FormField'

const RegisterForm = ({
	handleSubmit,
	submitting,
	error,
	team,
	isInvite,
	t,
	...props
}) => {
	const [teamUrl, setTeamUrl] = useState('')
	const [teamUrlTouched, setTeamUrlTouched] = useState(false)

	const [{ error: postError }, executePost] = useAxios(
		{
			url: 'auth/check-slug',
			method: 'POST',
		},
		{ manual: true }
	)

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!teamUrl.length) return false

			executePost({
				data: {
					slug: teamUrl,
				},
			})
		}, 350)

		return () => clearTimeout(timeout)
	}, [teamUrl])

	const uniqueSlug = useMemo(
		() => () => (postError ? postError.response.data.message : undefined),
		[postError]
	)

	return (
		<div className="u-center-inside-flex u-max-width--small">
			<Form
				onSubmit={handleSubmit}
				actions={
					<Button
						layout="secondary"
						fullWidth
						centerContent
						text={t('forms.create_your_account')}
						type="submit"
						disabled={submitting}
					/>
				}
				error={error}
			>
				<>
					<h1>
						{t('forms.sign_up_for_free')}
						<Text size="small">
							{t('or')}&nbsp;
							<Button
								layout="underlined-text"
								text={t('forms.sign_in_to_your_account')}
								onClick={() => history.push('/login')}
							/>
						</Text>
					</h1>
					{team && (
						<Notice
							title={t('forms.invitation')}
							text={<>{t('forms.invitation_register_text')}</>}
						/>
					)}
					{!team && (
						<FormFieldset>
							<Field
								name="voucherCode"
								label={t('forms.beta_key')}
								component={TextInput}
								validate={[required]}
								disabled={submitting}
							/>
							<Text size="small">
								{t('forms.not_signed_up_for_beta')}&nbsp;
								<Button
									layout="underlined-text"
									text={t(
										'forms.not_signed_up_for_beta_link'
									)}
									href="https://skara.io/"
									target="_blank"
								/>
							</Text>
						</FormFieldset>
					)}
					<FormFieldset legend={t('forms.your_team')}>
						<Field
							name="team"
							label={t('forms.team_name')}
							component={TextInput}
							validate={[required]}
							readOnly={!!team}
							onChange={e => {
								if (!teamUrlTouched) {
									props.changeFieldValue(
										'register',
										'teamUrl',
										createSlug(e.target.value)
									)

									setTeamUrl(createSlug(e.target.value))
								}
							}}
							disabled={submitting}
						/>
						<Field
							name="teamUrl"
							label={t('forms.team_url')}
							component={Url}
							validate={[required, uniqueSlug]}
							readOnly={!!team}
							onChange={e => {
								props.changeFieldValue(
									'register',
									'teamUrl',
									createSlug(e.target.value)
								)

								setTeamUrl(createSlug(e.target.value))

								if (createSlug(e.target.value).length > 0) {
									setTeamUrlTouched(true)
								} else {
									setTeamUrlTouched(false)
								}
							}}
							disabled={submitting}
						/>
					</FormFieldset>
					<FormFieldset legend={t('forms.your_profile')}>
						<Field
							name="firstName"
							label={t('forms.first_name')}
							component={TextInput}
							validate={[required]}
							disabled={submitting}
						/>
						<Field
							name="lastName"
							label={t('forms.last_name')}
							component={TextInput}
							validate={[required]}
							disabled={submitting}
						/>
						<Field
							name="email"
							label={t('forms.email')}
							component={Email}
							validate={[required, email]}
							readOnly={isInvite}
							disabled={submitting}
						/>
						<Field
							name="password"
							label={t('forms.password')}
							component={Password}
							validate={[required, minLength6]}
							disabled={submitting}
						/>
					</FormFieldset>
					<FormFieldset>
						<Text size="small">
							{t('forms.by_registering_you_agree_to_our')}&thinsp;
							<Button
								layout="underlined-text"
								text={t('privacy_policy')}
								href="https://skara.io/privacy-policy/"
								target="_blank"
							/>
							,&thinsp;
							<Button
								layout="underlined-text"
								text={t('cookie_policy')}
								href="https://skara.io/cookie-policy/"
								target="_blank"
							/>
							&thinsp;{t('and')}&thinsp;
							<Button
								layout="underlined-text"
								text={t('terms_of_service')}
								href="https://skara.io/terms-of-service/"
								target="_blank"
							/>
							.
						</Text>
					</FormFieldset>
				</>
			</Form>
		</div>
	)
}

const mapDispatchToProps = dispatch =>
	bindActionCreators({ changeFieldValue }, dispatch)

export default connect(
	null,
	mapDispatchToProps
)(
	reduxForm(
		{
			form: 'register',
			onSubmitFail: errors => {
				const firstError = Object.keys(errors)[0]
				const el = document.querySelector(`[name="${firstError}"]`)

				el.scrollIntoView({
					block: 'center',
					behavior: 'smooth',
				})
			},
		},
		mapDispatchToProps
	)(translate()(RegisterForm))
)
